<template>
	<FileManager v-if="showFileManager" v-model:selected-file="selectedFile.src" @picked="showImagePropertiesModal" @close="showFileManager = false"/>
	<Modal :id="imagePropertiesId">
		<template #title>{{ trans('general.file_manager.images.insert') }}</template>
		<div class="flex flex-col gap-3">
			<p class="text-gray-400 text-sm">{{ trans('general.file_manager.images.help') }}</p>
			<div class="flex gap-3">
				<LabeledObject full>
					<template #label>{{ trans('general.file_manager.images.width') }}</template>
					<TextInput v-model="selectedFile.width"/>
				</LabeledObject>
				<LabeledObject full>
					<template #label>{{ trans('general.file_manager.images.height') }}</template>
					<TextInput v-model="selectedFile.height"/>
				</LabeledObject>
			</div>
		</div>
		<template #footer>
			<PrimaryButton :color="Color.Green" @click.stop="insertImage">
				<i class="fa-solid fa-check"/>
			</PrimaryButton>
		</template>
	</Modal>
	<Modal :id="linkManagerId">
		<template #title>{{ trans('general.text_editor.links.insert') }}</template>
		<div class="flex gap-3">
			<LabeledObject full>
				<template #label>{{ trans('general.text_editor.links.url') }}</template>
				<TextInput v-model="link"/>
			</LabeledObject>
		</div>
		<template #footer>
			<SoftButton :color="Color.Red" @click.stop="unsetLink">
				<i class="fa-solid fa-link-slash"/>
			</SoftButton>
			<PrimaryButton :color="Color.Green" @click.stop="setLink">
				<i class="fa-solid fa-check mr-2"/>
				{{ trans('general.confirm') }}
			</PrimaryButton>
		</template>
	</Modal>
	<div v-if="editor" :class="`grow flex flex-col ${bordered ? 'border rounded-lg border-gray-200 dark:border-gray-800' : ''}`">
		<div :class="`py-2 flex items-center flex-wrap gap-y-2 divide-x dark:divide-gray-800 ${bordered ? 'rounded-t-lg' : ''} bg-gray-100 dark:bg-gray-900 select-none`">
			<div class="px-3 flex items-center gap-3">
				<Dropdown id="font-style">
					<template #button>
						<TextEditorButton :title="trans('general.editor.styles.title')">
							<i class="fa-solid fa-heading"/>
						</TextEditorButton>
					</template>
					<DropdownItem @click.stop="editor.chain().focus().setHeading({level: 1}).run()">{{ trans('general.editor.styles.h1') }}</DropdownItem>
					<DropdownItem @click.stop="editor.chain().focus().setHeading({level: 2}).run()">{{ trans('general.editor.styles.h2') }}</DropdownItem>
					<DropdownItem @click.stop="editor.chain().focus().setHeading({level: 3}).run()">{{ trans('general.editor.styles.h3') }}</DropdownItem>
					<DropdownItem @click.stop="editor.chain().focus().setHeading({level: 4}).run()">{{ trans('general.editor.styles.h4') }}</DropdownItem>
					<DropdownItem @click.stop="editor.chain().focus().setHeading({level: 5}).run()">{{ trans('general.editor.styles.h5') }}</DropdownItem>
					<DropdownItem @click.stop="editor.chain().focus().setHeading({level: 6}).run()">{{ trans('general.editor.styles.h6') }}</DropdownItem>
					<DropdownItem @click.stop="editor.chain().focus().setParagraph().run()">{{ trans('general.editor.styles.p') }}</DropdownItem>
				</Dropdown>
				<Dropdown id="font-family">
					<template #button>
						<TextEditorButton :title="trans('general.editor.families.title')">
							<i class="fa-solid fa-font"/>
						</TextEditorButton>
					</template>
					<DropdownItem class="font-serif" @click.stop="editor.chain().focus().setFontFamily('serif').run()">{{ trans('general.editor.families.serif') }}</DropdownItem>
					<DropdownItem class="font-sans-serif" @click.stop="editor.chain().focus().setFontFamily('sans-serif').run()">{{ trans('general.editor.families.sans-serif') }}</DropdownItem>
					<DropdownItem class="font-mono" @click.stop="editor.chain().focus().setFontFamily('monospace').run()">{{ trans('general.editor.families.monospace') }}</DropdownItem>
					<DropdownItem class="font-cursive" @click.stop="editor.chain().focus().setFontFamily('cursive').run()">{{ trans('general.editor.families.cursive') }}</DropdownItem>
					<DropdownSeparator/>
					<DropdownItem class="font-[Titillium_Web]" @click.stop="editor.chain().focus().setFontFamily('Titillium Web').run()">Titillium Web</DropdownItem>
					<DropdownItem class="font-[Merriweather]" @click.stop="editor.chain().focus().setFontFamily('Merriweather').run()">Merriweather</DropdownItem>
					<DropdownItem class="font-[Menlo]" @click.stop="editor.chain().focus().setFontFamily('Menlo').run()">Menlo</DropdownItem>
					<DropdownItem class="font-[Inter]" @click.stop="editor.chain().focus().setFontFamily('Inter').run()">Inter</DropdownItem>
					<DropdownItem class="font-[Montserrat]" @click.stop="editor.chain().focus().setFontFamily('Montserrat').run()">Montserrat</DropdownItem>
					<DropdownItem class="font-[Roboto]" @click.stop="editor.chain().focus().setFontFamily('Roboto').run()">Roboto</DropdownItem>
					<DropdownItem class="font-[Noto_Sans]" @click.stop="editor.chain().focus().setFontFamily('Noto Sans').run()">Noto Sans</DropdownItem>
				</Dropdown>
			</div>
			<div class="px-3 flex items-center gap-3">
				<TextEditorButton :title="trans('general.editor.format.bold')" :is-active="editor.isActive('bold')" @click.stop="editor.chain().focus().toggleBold().run()">
					<i class="fa-solid fa-bold"/>
				</TextEditorButton>
				<TextEditorButton :title="trans('general.editor.format.italic')" :is-active="editor.isActive('italic')" @click.stop="editor.chain().focus().toggleItalic().run()">
					<i class="fa-solid fa-italic"/>
				</TextEditorButton>
				<TextEditorButton :title="trans('general.editor.format.underline')" :is-active="editor.isActive('underline')" @click.stop="editor.chain().focus().toggleUnderline().run()">
					<i class="fa-solid fa-underline"/>
				</TextEditorButton>
				<TextEditorButton :title="trans('general.editor.format.strikethrough')" :is-active="editor.isActive('strike')" @click.stop="editor.chain().focus().toggleStrike().run()">
					<i class="fa-solid fa-strikethrough"/>
				</TextEditorButton>
			</div>
			<div class="px-3 flex items-center gap-3">
				<TextEditorButton :title="trans('general.editor.alignment.left')" :is-active="editor.isActive({textAlign: 'left'})" @click.stop="editor.chain().focus().setTextAlign('left').run()">
					<i class="fa-solid fa-align-left"/>
				</TextEditorButton>
				<TextEditorButton :title="trans('general.editor.alignment.center')" :is-active="editor.isActive({textAlign: 'center'})" @click.stop="editor.chain().focus().setTextAlign('center').run()">
					<i class="fa-solid fa-align-center"/>
				</TextEditorButton>
				<TextEditorButton :title="trans('general.editor.alignment.right')" :is-active="editor.isActive({textAlign: 'right'})" @click.stop="editor.chain().focus().setTextAlign('right').run()">
					<i class="fa-solid fa-align-right"/>
				</TextEditorButton>
				<TextEditorButton :title="trans('general.editor.alignment.justify')" :is-active="editor.isActive({textAlign: 'justify'})" @click.stop="editor.chain().focus().setTextAlign('justify').run()">
					<i class="fa-solid fa-align-justify"/>
				</TextEditorButton>
			</div>
			<div class="px-3 flex items-center gap-3">
				<TextEditorButton :title="trans('general.editor.baseline.subscript')" :is-active="editor.isActive('subscript')" @click.stop="editor.chain().focus().toggleSubscript().run()">
					<i class="fa-solid fa-subscript"/>
				</TextEditorButton>
				<TextEditorButton :title="trans('general.editor.baseline.superscript')" :is-active="editor.isActive('superscript')" @click.stop="editor.chain().focus().toggleSuperscript().run()">
					<i class="fa-solid fa-superscript"/>
				</TextEditorButton>
				<TextEditorButton :title="trans('general.editor.blocks.quote')" :is-active="editor.isActive('blockquote')" @click.stop="editor.chain().focus().toggleBlockquote().run()">
					<i class="fa-solid fa-quote-left"/>
				</TextEditorButton>
				<TextEditorButton :title="trans('general.editor.blocks.code')" :is-active="editor.isActive('codeBlock')" @click.stop="editor.chain().focus().toggleCodeBlock().run()">
					<i class="fa-solid fa-code"/>
				</TextEditorButton>
			</div>
			<div class="px-3 flex items-center gap-3">
				<TextEditorButton :title="trans('general.editor.lists.bulleted')" :is-active="editor.isActive('bulletList')" @click.stop="editor.chain().focus().toggleBulletList().run()">
					<i class="fa-solid fa-list-dots"/>
				</TextEditorButton>
				<TextEditorButton :title="trans('general.editor.lists.numbered')" :is-active="editor.isActive('orderedList')" @click.stop="editor.chain().focus().toggleOrderedList().run()">
					<i class="fa-solid fa-list-ol"/>
				</TextEditorButton>
				<TextEditorButton :title="trans('general.editor.external.image')" @click.stop="showFileManager = true">
					<i class="fa-solid fa-image"/>
				</TextEditorButton>
				<TextEditorButton :title="trans('general.editor.external.link')" :is-active="editor.isActive('link')" @click.stop="showLinkManager">
					<i class="fa-solid fa-link"/>
				</TextEditorButton>
			</div>
			<div class="px-3 flex items-center gap-3">
				<TextEditorButton :title="trans('general.editor.extra.divider')" @click.stop="editor.chain().focus().setHorizontalRule().run()">
					<i class="fa-solid fa-grip-lines"/>
				</TextEditorButton>
				<Dropdown id="text-color">
					<template #button>
						<TextEditorButton :title="trans('general.editor.extra.text-color')">
							<i class="fa-solid fa-droplet"/>
						</TextEditorButton>
					</template>
					<ColorPicker input-id="text-color-input" format="hex" inline @update:model-value="setColor($event)"/>
				</Dropdown>
				<Dropdown id="text-highlight">
					<template #button>
						<TextEditorButton :title="trans('general.editor.extra.background-color')">
							<i class="fa-solid fa-highlighter"/>
						</TextEditorButton>
					</template>
					<ColorPicker input-id="text-highlight-input" format="hex" inline @update:model-value="setHighlight($event)"/>
				</Dropdown>
			</div>
			<div :class="{'px-3 flex items-center gap-3': true, 'character-count--warning': editor.storage.characterCount.characters() === limit}">
				<svg height="20" width="20" viewBox="0 0 20 20">
					<circle r="10" cx="10" cy="10" class="fill-gray-200"/>
					<circle r="5" cx="10" cy="10" class="fill-transparent stroke-purple-500" stroke-width="10" :stroke-dasharray="`calc(${percentage} * 31.4 / 100) 31.4`" transform="rotate(-90) translate(-20)"/>
					<circle class="fill-gray-100 dark:fill-gray-800" r="6" cx="10" cy="10"/>
				</svg>
				<p class="px-2 py-1 rounded bg-gray-200 dark:bg-gray-800">{{ editor.storage.characterCount.characters() }} / {{ limit }} {{ trans('general.characters') }}</p>
				<p class="px-2 py-1 rounded bg-gray-200 dark:bg-gray-800">{{ editor.storage.characterCount.words() }} {{ trans('general.words') }}</p>
			</div>
			<slot name="actions"/>
		</div>
		<EditorContent class="grow flex flex-col bg-gray-50 dark:bg-gray-950 rounded-b-lg" :editor="editor"/>
	</div>
</template>
<script setup lang="ts">
import {trans} from 'laravel-vue-i18n'
import Link from '@tiptap/extension-link'
import {Color} from '@/Types/Enums/Color'
import StarterKit from '@tiptap/starter-kit'
import ColorPicker from 'primevue/colorpicker'
import Modal from '@/Components/UI/Modals/Modal.vue'
import Highlight from '@tiptap/extension-highlight'
import Underline from '@tiptap/extension-underline'
import Subscript from '@tiptap/extension-subscript'
import {Editor, EditorContent} from '@tiptap/vue-3'
import TextAlign from '@tiptap/extension-text-align'
import TextStyle from '@tiptap/extension-text-style'
import FontFamily from '@tiptap/extension-font-family'
import Superscript from '@tiptap/extension-superscript'
import {closeModal, showModal, storage} from '@/Helpers/general'
import Dropdown from '@/Components/UI/Dropdown/Dropdown.vue'
import TextInput from '@/Components/UI/Inputs/TextInput.vue'
import LabeledObject from '@/Components/UI/LabeledObject.vue'
import {Color as TipTapColor} from '@tiptap/extension-color'
import SoftButton from '@/Components/UI/Buttons/SoftButton.vue'
import FileManager from '@/Components/Files/FileManager.vue'
import CharacterCount from '@tiptap/extension-character-count'
import DropdownItem from '@/Components/UI/Dropdown/DropdownItem.vue'
import TextEditorImage from '@/Components/UI/Editor/TextEditorImage'
import PrimaryButton from '@/Components/UI/Buttons/PrimaryButton.vue'
import TextEditorButton from '@/Components/UI/Editor/TextEditorButton.vue'
import DropdownSeparator from '@/Components/UI/Dropdown/DropdownSeparator.vue'
import {computed, type ComputedRef, defineEmits, defineProps, onBeforeUnmount, onMounted, ref, type Ref} from 'vue'

const props = defineProps<{
	id?:string,
	bordered?:boolean,
	modelValue:string,
	automaticSave?:boolean,
}>()

const emit = defineEmits([
	'update:modelValue',
	'save',
])

const percentage:ComputedRef<number> = computed(() => {
	if(editor.value && limit.value > 0){
		return Math.round((100 / limit.value) * editor.value.storage.characterCount.characters())
	}
	return 0
})

const selectedFile:Ref<{src:string, width:string, height:string}> = ref({
	src: '',
	width: '100%',
	height: 'auto',
})
const link:Ref<string> = ref('')
const limit:Ref<number> = ref(65535)
const editor:Ref<Editor|null> = ref(null)
const showFileManager:Ref<boolean> = ref(false)
const text:Ref<string|undefined> = ref(props.modelValue)
const automaticSave:Ref<boolean> = ref(props.automaticSave)
const linkManagerId:Ref<string> = ref(`${props.id}-link-manager`)
const updateTimeoutId:Ref<ReturnType<typeof setTimeout>|null> = ref(null)
const imagePropertiesId:Ref<string> = ref(`${props.id}-image-properties-modal`)

function setColor(hex:string){
	if(editor.value){
		editor.value.chain().focus().setColor(`#${hex}`).run()
	}
}

function setHighlight(hex:string){
	if(editor.value){
		editor.value.chain().focus().setHighlight({
			color: `#${hex}`,
		}).run()
	}
}

function showImagePropertiesModal(){
	showFileManager.value = false
	showModal(imagePropertiesId.value)
}

function insertImage(){
	if(editor.value){
		editor.value.chain().focus().setImage({
			src: storage(selectedFile.value.src),
			height: selectedFile.value.height,
			width: selectedFile.value.width,
		}).run()
		selectedFile.value.src = ''
		selectedFile.value.width = '100%'
		selectedFile.value.height = 'auto'
		closeModal(imagePropertiesId.value)
	}
}

function showLinkManager(){
	link.value = editor.value?.getAttributes('link').href
	showModal(linkManagerId.value)
}

function setLink(){
	if(link.value.length === 0){
		unsetLink()
	} else {
		editor.value?.chain().focus().extendMarkRange('link').setLink({
			href: link.value,
		}).run()
		link.value = ''
		closeModal(linkManagerId.value)
	}
}

function unsetLink(){
	link.value = ''
	editor.value?.chain().focus().extendMarkRange('link').unsetLink().run()
	closeModal(linkManagerId.value)
}

onMounted(() => {
	editor.value = new Editor({
		content: text.value,
		editorProps: {
			attributes: {
				class: 'wysiwyg-content',
			},
		},
		extensions: [
			TextStyle,
			Underline,
			Subscript,
			StarterKit,
			FontFamily,
			Superscript,
			TipTapColor,
			TextEditorImage,
			CharacterCount.configure({
				limit: limit.value,
			}),
			Link.configure({
				openOnClick: false,
				defaultProtocol: 'https',
			}),
			Highlight.configure({
				multicolor: true,
			}),
			TextAlign.configure({
				types: [
					'heading',
					'paragraph',
				],
			}),
		],
		onUpdate: () => {
			emit('update:modelValue', editor.value?.getHTML() ?? '')
			if(automaticSave.value){
				if(updateTimeoutId.value){
					clearTimeout(updateTimeoutId.value)
				}
				updateTimeoutId.value = setTimeout(() => {
					emit('save')
					updateTimeoutId.value = null
				}, 5000)
			}
		},
	})

})

onBeforeUnmount(() => {
	editor.value?.destroy()
})
</script>